// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/api/client";
import type { components } from "@/api/schema";

// --- Types ---

export type ProductImagesChainOption = components["schemas"]["ProductImageChainOptionResponse"];
export type ProductImagesOptions = components["schemas"]["ProductImageOptionsResponse"];
export type ProductImageFile = components["schemas"]["ProductImageFileResponse"];
export type ProductImageFileValidation = components["schemas"]["ProductImageFileValidationEnum"];
export type ProductImageSummary = components["schemas"]["ProductImageSummaryResponse"];
export type ProductImagePrecheck = components["schemas"]["ProductImagePrecheckResponse"];
export type ProductImageBatch = components["schemas"]["ProductImageBatchResponse"];
export type ProductImageBatchError = components["schemas"]["ProductImageBatchErrorResponse"];
export type ProductImageBatchStatus = components["schemas"]["ImportEntryStatusEnum"];
export type ProductImageBatchesSortBy = components["schemas"]["ProductImageBatchesSortByEnum"];
export type ProductImageUserRef = components["schemas"]["UserRelevantResponse"];

export interface ProductImageBatchesResult {
  batches: ProductImageBatch[];
  users: ProductImageUserRef[];
  count: number;
}

/** Scope of a precheck or a batch: the chain, the store whose catalog maps the names, and the prefix rule. */
export interface ProductImagesScope {
  chainName: string;
  referenceStoreName: string;
  /** Overrides the chain's configured prefix; omitted for chains that match by barcode. */
  countryPrefix?: string;
}

// --- Options ---

export function useProductImagesOptionsQuery(params: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: ["product-images", "options"],
    enabled: params.enabled,
    queryFn: async () => {
      const res = await api.GET("/v1/ingress/product-images/options");
      return res.data as unknown as ProductImagesOptions;
    },
  });
}

// --- Batches ---

interface ProductImageBatchesParams {
  page?: number;
  limit?: number;
  sortBy?: ProductImageBatchesSortBy;
  orderBy?: "asc" | "desc";
  search?: string;
  chainName?: string;
  status?: string;
  /** Keep refetching while a batch on the page is still uploading, publishing or being resized. */
  pollWhileProcessing?: boolean;
}

// A batch that is still in the pipeline changes every few seconds; the poll keeps the previous page on
// screen so rows do not flicker while the next one loads.
const PROCESSING_POLL_MS = 10000;

export function useProductImageBatchesQuery(params: ProductImageBatchesParams = {}) {
  const { page = 1, limit = 20, sortBy, orderBy, search, chainName, status, pollWhileProcessing } = params;

  return useQuery({
    queryKey: ["product-images", "batches", { page, limit, sortBy, orderBy, search, chainName, status }],
    placeholderData: keepPreviousData,
    refetchInterval: pollWhileProcessing
      ? (query) =>
          query.state.data?.batches.some((batch) => batch.status === "processing") ? PROCESSING_POLL_MS : false
      : undefined,
    queryFn: async () => {
      const res = await api.GET("/v1/ingress/product-images/batches", {
        params: {
          query: {
            page,
            limit,
            sortBy,
            orderBy,
            search: search || undefined,
            "filter[chainName]": chainName || undefined,
            "filter[status]": (status || undefined) as ProductImageBatchStatus | undefined,
          },
        },
      });
      return res.data as unknown as ProductImageBatchesResult;
    },
  });
}

export function useProductImageBatchQuery(importRequestId: number | null) {
  return useQuery({
    queryKey: ["product-images", "batch", importRequestId],
    enabled: importRequestId != null,
    queryFn: async () => {
      const res = await api.GET("/v1/ingress/product-images/batches/{import_request_id}", {
        params: { path: { import_request_id: importRequestId as number } },
      });
      return res.data as unknown as ProductImageBatch;
    },
  });
}

// --- Wizard mutations ---

/** Name-only dry run against the reference store catalog. Nothing is uploaded or stored. */
export function usePrecheckProductImagesMutation() {
  return useMutation({
    mutationFn: async (params: ProductImagesScope & { fileNames: string[] }) => {
      const res = await api.POST("/v1/ingress/product-images/precheck", { body: params });
      if (res.error) throw new Error("Failed to check product image file names");
      return res.data as unknown as ProductImagePrecheck;
    },
  });
}

/** The approval step: records the batch with every checked file name, so the mapped files can be uploaded. */
export function useCreateProductImageBatchMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: ProductImagesScope & { fileNames: string[] }) => {
      const res = await api.POST("/v1/ingress/product-images/batches", { body: params });
      if (res.error) throw new Error("Failed to create product image batch");
      return res.data as unknown as ProductImageBatch;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["product-images", "batches"] });
    },
  });
}

/** Uploads one mapped file of a batch to its staging folder; nothing is published until the batch is. */
export function useUploadProductImageFileMutation() {
  return useMutation({
    mutationFn: async (params: { importRequestId: number; file: File }) => {
      const fd = new FormData();
      fd.append("uploadFile", params.file);
      const res = await api.POST("/v1/ingress/product-images/batches/{import_request_id}/files", {
        params: { path: { import_request_id: params.importRequestId } },
        body: fd as never,
        bodySerializer: () => fd,
      });
      if (res.error) throw new Error(`Failed to upload ${params.file.name}`);
      return res.data as unknown as ProductImageFile;
    },
  });
}

/**
 * Starts the background job that publishes the uploaded files of a batch. Idempotent: files already
 * published are skipped, so a batch whose job died can be published again from the list.
 */
export function usePublishProductImageBatchMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (importRequestId: number) => {
      const res = await api.POST("/v1/ingress/product-images/batches/{import_request_id}/publish", {
        params: { path: { import_request_id: importRequestId } },
      });
      if (res.error) throw new Error("Failed to start publishing the product image batch");
      return res.data as unknown as ProductImageBatch;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["product-images", "batches"] });
    },
  });
}
